/**
 * RoomBackdrop3D — the lazy-loaded, failure-guarded entry point for the
 * waiting-room's live 3D tavern-bar backdrop.
 *
 * This wrapper is deliberately Three.js-FREE: it `React.lazy`s the heavy
 * {@link RoomBackdrop3DScene} (which statically imports `three` + `@tt/shell-3d`)
 * so the WebGL chunk only downloads once a player is actually sitting in a
 * room — it never weighs down the Home / Gate / first-paint bundle. Until the
 * chunk resolves (and on any WebGL/chunk failure) nothing renders here and the
 * existing 2D {@link TavernBackdrop} SVG shows through underneath, so the room
 * is never blank.
 *
 * Use THIS from Room.tsx, never the scene module directly.
 */
import { Suspense, lazy } from 'react';
import type { LobbyPlayerView } from '@tt/lobby';
import { Lobby3DBoundary } from './Lobby3DBoundary';
import type { MoveInput, RemoteMove } from './RoomBackdrop3DScene';
import type { TavernConfig } from './tavernDecor';

const RoomBackdrop3DScene = lazy(() => import('./RoomBackdrop3DScene'));

export interface RoomBackdrop3DProps {
  /** Live (non-spectator) players to seat around the bar. */
  players: LobbyPlayerView[];
  /**
   * How strongly to calm the hall behind the central content column (0..1).
   * The waiting room's default (0.22) keeps the seated table lush; the denser
   * between-rounds + ceremony screens pass a higher value so the score sheet /
   * podium / standings stay crisp over the hall.
   */
  contentDim?: number;
  /** Local player's charId — that figure becomes walk-controllable (waiting room). */
  meCharId?: string;
  /** Live input ref for the local walk (WASD / joystick). */
  inputRef?: React.MutableRefObject<MoveInput | null>;
  /** Throttled callback with the local figure's world position while moving. */
  onLocalMove?: (x: number, z: number, yaw: number, moving: boolean) => void;
  /** Register for inbound remote moves; returns an unsubscribe fn. */
  subscribeMoves?: (cb: (m: RemoteMove) => void) => () => void;
  /** Trigger a hall toy over the wire (waiting room). */
  sendToyUse?: (toyId: string, seed: number) => void;
  /** Register for inbound remote hall-toy triggers; returns an unsubscribe fn. */
  subscribeToys?: (cb: (t: { charId: string; name: string; toyId: string; seed: number }) => void) => () => void;
  /** Hall-as-lobby (8P Phase 1): nameplates above heads + tap-to-inspect
   *  + chat bubbles above the speaker. The waiting room passes these. */
  nameplates?: boolean;
  onInspectPlayer?: (charId: string) => void;
  chat?: Array<{ charId: string; text: string; at: string | number }>;
  reactions?: Array<{ id: number; charId: string; emoji: string }>;
  dimPlates?: boolean;
  /** The HOST's "Your Own Tavern" decor config — see RoomBackdrop3DScene's
   *  doc. Room.tsx fetches this once per room (host's charId) and passes
   *  it down; omitted ⇒ default hall. */
  tavernConfig?: TavernConfig;
  /** Phase B2 — the jukebox's current pick (waiting room only; Room.tsx
   *  gates this to `lobby.state === 'waiting'`). Drives the brass
   *  "now playing" plate + the cabinet's emissive pulse. Null/omitted ⇒
   *  no plate, no pulse. */
  jukeboxNowPlaying?: { name: string; recordLabel: string } | null;
  /** "Walk Through the Door" reverse (Own Tavern §3): on the first build after
   *  returning from a match, remote players walk in through the doorway. */
  entryWalk?: boolean;
}

export function RoomBackdrop3D({ players, contentDim = 0.22, meCharId, inputRef, onLocalMove, subscribeMoves, sendToyUse, subscribeToys, nameplates, onInspectPlayer, chat, reactions, dimPlates, tavernConfig, jukeboxNowPlaying, entryWalk }: RoomBackdrop3DProps) {
  return (
    <Lobby3DBoundary>
      <Suspense fallback={null}>
        <RoomBackdrop3DScene
          variant="room"
          players={players}
          contentDim={contentDim}
          {...(meCharId ? { meCharId } : {})}
          {...(inputRef ? { inputRef } : {})}
          {...(onLocalMove ? { onLocalMove } : {})}
          {...(subscribeMoves ? { subscribeMoves } : {})}
          {...(sendToyUse ? { sendToyUse } : {})}
          {...(subscribeToys ? { subscribeToys } : {})}
          {...(nameplates ? { nameplates } : {})}
          {...(onInspectPlayer ? { onInspectPlayer } : {})}
          {...(chat ? { chat } : {})}
          {...(reactions ? { reactions } : {})}
          {...(dimPlates !== undefined ? { dimPlates } : {})}
          {...(tavernConfig ? { tavernConfig } : {})}
          {...(jukeboxNowPlaying !== undefined ? { jukeboxNowPlaying } : {})}
          {...(entryWalk ? { entryWalk } : {})}
        />
      </Suspense>
    </Lobby3DBoundary>
  );
}
